import React from 'react';
import useCheckoutState from '../../hooks/useCheckoutState';
import useCheckoutFormHelpers from '../../hooks/useCheckoutFormHelpers';
import useAiTarget from '../../hooks/useAiTarget';
import { isMultiPrice } from '../../lib/multiPrice';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import PricePicker from '../PricePicker/PricePicker';
import StyledElement from '../StyledElement';

/**
 * Self-contained multi-price picker: pulls everything from checkout state so
 * the same component renders either inside the Product block (position
 * 'content' — the default and the original behavior) or in the layout's form
 * area (position 'form' — mirroring the variants position option). Renders
 * nothing on single-price products.
 */
export default function ProductPricePicker({ className = 'mt-4' }) {
  const {
    checkout,
    query,
    theme,
    selectedPriceId,
    setSelectedPriceId,
    pendingPwywFocusRef,
    currencyLocale,
  } = useCheckoutState();
  const { setFormStateByKey } = useCheckoutFormHelpers();
  const aiTarget = useAiTarget();
  const product = checkout.product;

  // Switching to a different price clears the single pay-what-you-want amount so
  // the newly selected option's input starts empty instead of inheriting the
  // previous price's value (matches variant behaviour). Typing in a PWYW option
  // re-sets payWhatYouWant in the same handler, so the typed value still sticks.
  const handleSelect = priceId => {
    if (String(priceId) !== String(selectedPriceId)) {
      setFormStateByKey('payWhatYouWant', '');
    }
    setSelectedPriceId(priceId);
  };

  const handlePwywOptionActivate = priceId => {
    if (pendingPwywFocusRef) {
      pendingPwywFocusRef.current = { priceId: String(priceId), ts: Date.now() };
    }
  };

  if (!isMultiPrice(product?.prices, query)) return null;

  // Respect the Price options element visibility toggle (the eye icon in the
  // design panel). This is independent of the single-price block's toggle
  // (product.pricing.enabled) — the picker and the single price are separate
  // elements, each with its own enabled flag.
  if (theme?.elements?.product?.pricePicker?.enabled === false) return null;

  const labels = getThemeLabels(theme);

  // Layout settings come from the theme; the product only carries the tab
  // content maps (labels / defaults / order). The theme stores layout as a
  // single value ('list' | 'grid-N', N = 2..10) mapped to the picker's
  // priceDisplay + gridColumns props here.
  const pickerLayout = theme?.elements?.product?.pricePicker || {};
  const isGrid = typeof pickerLayout.layout === 'string' && pickerLayout.layout.startsWith('grid-');
  const gridColumns = isGrid
    ? Math.min(Math.max(parseInt(pickerLayout.layout.slice('grid-'.length), 10) || 2, 2), 10)
    : 2;

  // Selected-mark shape for the radio indicator, stored as a style on the
  // indicator element (class-driven in PricePicker — see checkout.css).
  const indicatorShape = pickerLayout.option?.indicator?.styles?.shape || 'dot';

  // The heading, description and Show more setting are the seller's product
  // settings (Product → Prices → Settings), not theme values. An untouched
  // product shortens long descriptions.
  const pickerSettings = product.pricePicker || {};
  const shortenDescription = pickerSettings.shortenDescription !== false;
  // Gate on the theme's eye toggles here as well as in StyledElement, or a
  // hidden pair would leave an empty wrapper carrying its bottom margin.
  const heading = pickerLayout.title?.enabled !== false ? pickerSettings.title?.trim() : '';
  const headingDescription =
    pickerLayout.description?.enabled !== false ? pickerSettings.description?.trim() : '';

  return (
    <div className={className}>
      {/* The design editor outlines this block, so it stays free of the padding
          form-area callers pass in className. */}
      <div className="cp_price-picker-block" {...aiTarget('pricePicker')}>
        {(heading || headingDescription) && (
          <div className="cp_price-picker-header space-y-1 [margin-bottom:var(--cp_price-picker-title-padding-bottom)]">
            {heading && (
              <StyledElement
                elementPath="product.pricePicker.title"
                as="h3"
                className="cp_price-picker-title"
              >
                {heading}
              </StyledElement>
            )}
            {headingDescription && (
              <StyledElement
                elementPath="product.pricePicker.description"
                as="div"
                className="cp_price-picker-description whitespace-pre-line break-words"
              >
                {headingDescription}
              </StyledElement>
            )}
          </div>
        )}
        <PricePicker
          prices={product.prices}
          query={query}
          allowDynamicPrice={checkout.allowDynamicPrice}
          allowDynamicDiscountedFromPrice={checkout.allowDynamicDiscountedFromPrice}
          allowDynamicLabel={checkout.allowDynamicLabel}
          allowDynamicDescription={checkout.allowDynamicDescription}
          allowDynamicPlanIterations={checkout.allowDynamicPlanIterations}
          defaultPriceId={product.defaultPriceId}
          selectedPriceId={selectedPriceId}
          onSelect={handleSelect}
          onPwywOptionActivate={handlePwywOptionActivate}
          labels={labels}
          currencyLocale={currencyLocale}
          groupBy={pickerLayout.groupBy}
          priceDisplay={isGrid ? 'grid' : 'list'}
          gridColumns={gridColumns}
          horizontalAlign={pickerLayout.horizontalAlign}
          verticalAlign={pickerLayout.verticalAlign}
          pwywDisplay={pickerLayout.pwywDisplay}
          indicatorShape={indicatorShape}
          shortenDescription={shortenDescription}
          groupingMode={pickerLayout.groupingMode}
          priceDisplayLabels={product.pricePicker?.priceDisplayLabels}
          priceDisplayDefaults={product.pricePicker?.priceDisplayDefaults}
          priceDisplayTabOrder={product.pricePicker?.priceDisplayTabOrder}
        />
      </div>
    </div>
  );
}
